import React from 'react'
import clsx from 'clsx'
import { useNavigate } from 'react-router'
import { alpha, Dialog } from '@mui/material'
import { makeStyles } from '@mui/styles'
import ThemeInterface from '@/theme-interface'
import lang from '@/lang'
import { brandFontScope, brandHeading, brandText } from '@/utils/brand-typography'
import { ButtonSquare, SquareIconButton } from '@/modules/base/components/buttons'
import { VARIANTS as BUTTON_VARIANTS } from '@/modules/base/components/buttons/square/button-square'
import { brandOutlineButton, brandPrimaryButton } from '@/modules/base/components/modal/upgrade-modal-v2/_helpers/brandButtons'
import CloseSvg from '@assets/images/icons/v2/close.svg'
import ExpertImage from '@assets/images/onboarding/welcome-expert.png'
import { registrationBackdrop } from '../helpers/registration-backdrop'

export const WELCOME_ROUTES = Object.freeze({
  firstInvoice: '/invoice/new',
  tour: '/firstSteps',
})

interface WelcomeModalProps {
  open?: boolean
  onClose: () => void
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  backdrop: {
    ...registrationBackdrop(theme),
  },
  paper: {
    width: 560,
    maxWidth: 'calc(100% - 32px)',
    margin: 16,
    padding: 8,
    borderRadius: theme.borderRadiusExtraExtraLarge,
    backgroundColor: theme.palette.background.paper,
    ...brandFontScope(theme),
    [theme.breakpoints.down(768)]: {
      width: '100%',
      maxWidth: '100%',
      height: '100%',
      maxHeight: '100%',
      margin: 0,
      padding: 0,
      borderRadius: 0,
    },
  },
  header: {
    position: 'relative',
    height: 263,
    flexShrink: 0,
    overflow: 'hidden',
    borderRadius: theme.borderRadiusExtraLarge,
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down(768)]: {
      height: 320,
      borderRadius: 0,
    },
  },
  image: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center 30%',
  },
  // the two soft shapes over the photo - a wide one along the bottom, a small one in the top right corner
  shapeBottom: {
    position: 'absolute',
    left: '-13%',
    bottom: -98,
    width: '126%',
    height: 223,
    borderRadius: '50%',
    background: `radial-gradient(closest-side, ${alpha(theme.palette.brand.text.primary, 0.55)}, ${alpha(theme.palette.brand.text.primary, 0)})`,
  },
  shapeCorner: {
    position: 'absolute',
    top: -47,
    right: -70,
    width: 185,
    height: 177,
    borderRadius: '50%',
    background: `radial-gradient(closest-side, ${alpha(theme.palette.brand.text.primary, 0.75)}, ${alpha(theme.palette.brand.text.primary, 0)})`,
  },
  expert: {
    position: 'absolute',
    left: '50%',
    bottom: 20,
    transform: 'translateX(-50%)',
    padding: '8px 14px',
    whiteSpace: 'nowrap',
    borderRadius: theme.borderRadiusPill,
    border: `1px solid ${alpha(theme.palette.brand.contrastText, 0.6)}`,
    backgroundColor: alpha(theme.palette.brand.contrastText, 0.15),
    color: theme.palette.brand.contrastText,
    ...brandText(theme, 'tiny'),
    lineHeight: 1.4,
    '@supports (backdrop-filter: blur(4px))': {
      backdropFilter: 'blur(4px)',
    },
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 1,
    '& svg, & path': {
      fill: theme.palette.brand.contrastText,
    },
    [theme.breakpoints.down(768)]: {
      right: 24,
    },
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: 32,
    textAlign: 'center',
    [theme.breakpoints.down(768)]: {
      padding: '24px 24px calc(24px + env(safe-area-inset-bottom))',
    },
  },
  overline: {
    margin: 0,
    color: theme.palette.brand.main,
    textTransform: 'uppercase',
    ...brandText(theme, 'small'),
  },
  title: {
    margin: '8px 0 0',
    color: theme.palette.brand.text.heading,
    ...brandHeading(theme, 'h5'),
  },
  lead: {
    margin: '8px 0 0',
    color: theme.palette.brand.text.secondary,
    ...brandText(theme, 'regular'),
  },
  actions: {
    display: 'flex',
    gap: 12,
    marginTop: 28,
    [theme.breakpoints.down(768)]: {
      // the main action comes first on mobile, at the bottom of the screen
      flexDirection: 'column-reverse',
      marginTop: 'auto',
      paddingTop: 24,
    },
  },
  button: {
    flex: '1 1 auto',
    width: 'auto',
    height: 48,
    padding: '0 24px',
    whiteSpace: 'nowrap',
    ...brandText(theme, 'regular'),
  },
  tourButton: {
    ...brandOutlineButton(theme),
  },
  invoiceButton: {
    ...brandPrimaryButton(theme),
  },
}))

/**
 * Last screen of the orgaMAX registration, after the short question. It can be skipped with the X.
 */
const WelcomeModal = ({ open = true, onClose }: WelcomeModalProps) => {
  const classes = useStyles()
  const navigate = useNavigate()
  const texts = lang.registration.welcome

  const goTo = (route: string) => {
    onClose()
    navigate(route)
  }

  return (
    <Dialog
      open={open}
      maxWidth={false}
      scroll='body'
      onClose={onClose}
      classes={{ paper: classes.paper }}
      slotProps={{ backdrop: { className: classes.backdrop } }}
      aria-labelledby='registration-welcome-title'
      data-qs-id='registration-welcome'>
      <div className={classes.header}>
        <img src={ExpertImage} alt='' className={classes.image} />
        <span className={classes.shapeBottom} aria-hidden='true' />
        <span className={classes.shapeCorner} aria-hidden='true' />
        <SquareIconButton
          variant='icon'
          icon={CloseSvg}
          customIconSize='16px'
          onClick={onClose}
          ariaLabel={lang.registration.close}
          buttonWrapperClass={classes.closeButton}
          dataQsId='registration-welcome-close'
        />
        <span className={classes.expert}>{texts.expert}</span>
      </div>
      <div className={classes.body}>
        <p className={classes.overline}>{texts.overline}</p>
        <h2 id='registration-welcome-title' className={classes.title}>{texts.title}</h2>
        <p className={classes.lead}>{texts.lead}</p>
        <div className={classes.actions}>
          <ButtonSquare
            variant={BUTTON_VARIANTS.outline}
            className={clsx(classes.button, classes.tourButton)}
            label={texts.startTour}
            onClick={() => goTo(WELCOME_ROUTES.tour)}
            dataQsId='registration-welcome-tour'
          />
          <ButtonSquare
            className={clsx(classes.button, classes.invoiceButton)}
            label={texts.firstInvoice}
            onClick={() => goTo(WELCOME_ROUTES.firstInvoice)}
            dataQsId='registration-welcome-first-invoice'
          />
        </div>
      </div>
    </Dialog>
  )
}

export default WelcomeModal
